import { Link } from "react-router-dom";
import { Plane, Mic, FileAudio, Gavel, ArrowRight } from "lucide-react";
import { HERO_IMG, OFFICER_IMG } from "@/lib/api";

const steps = [
  { icon: Mic, title: "Speak, don't type", body: "Officer Fredo Quist questions you aloud. You answer with your voice, exactly as you would at the counter." },
  { icon: FileAudio, title: "Every word on record", body: "Each answer is recorded and transcribed with timestamps so you can replay how you sounded." },
  { icon: Gavel, title: "AI-assessed for review", body: "At the end, an AI assessor grades clarity, consistency and credibility against the Geneza Travel SOP for the super admin." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-950 relative z-10" data-testid="landing-page">
      <header className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center justify-between px-6 lg:px-12 h-16">
          <div className="flex items-center gap-2 text-white font-cabinet font-extrabold text-lg">
            <Plane className="w-5 h-5 text-blue-500" /> E-immigration Practice Tool
          </div>
          <div className="flex items-center gap-3">
            <Link to="/sign-in" data-testid="landing-signin-btn" className="text-sm text-slate-300 hover:text-white transition-colors px-3 py-2">Sign in</Link>
            <Link to="/sign-up" data-testid="landing-signup-btn" className="text-sm bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 transition-colors">Sign up with Gmail</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-slate-800">
        <img src={HERO_IMG} alt="Airport terminal" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/30" />
        <div className="relative grid lg:grid-cols-12 gap-12 px-6 lg:px-12 py-24 lg:py-32">
          <div className="lg:col-span-7 fade-up">
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-blue-400 mb-6">Geneza Solutions Pvt. Ltd. · International Travel SOP</p>
            <h1 className="font-cabinet font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Next in line.<br /><span className="text-slate-500">Are you ready for the officer?</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
              A 20–30 minute voice interview with a realistic immigration officer, built on your team's real FAQ — purpose, host, funding, flights,
              role-specific questions and customs. Practise until your answers are clear, truthful and consistent.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/sign-up" data-testid="hero-start-btn" className="group inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 transition-colors">
                Start practice interview <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/sign-in" data-testid="hero-signin-btn" className="inline-flex items-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-200 px-6 py-3 transition-colors">
                I already have an account
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="backdrop-blur-xl bg-slate-900/70 border border-slate-700/50 shadow-[0_8px_32px_rgba(0,0,0,0.4)] p-6 max-w-sm lg:ml-auto">
              <div className="flex items-center gap-4">
                <img src={OFFICER_IMG} alt="Officer Fredo Quist" className="w-16 h-16 object-cover grayscale" />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">PORT OF ENTRY · DESK 04</p>
                  <p className="font-cabinet font-bold text-white text-xl">Officer Fredo Quist</p>
                </div>
              </div>
              <p className="mt-6 font-mono text-sm text-slate-300 leading-relaxed">“Good morning. Passport, please. What is the purpose of your visit?”</p>
              <div className="mt-6 flex items-end gap-1 h-8" aria-hidden="true">
                {Array.from({ length: 24 }).map((_, i) => (
                  <span key={i} className="eq-bar w-1 bg-blue-500/80" style={{ height: `${30 + ((i * 37) % 70)}%`, animationDelay: `${(i % 6) * 0.12}s` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-3 border-b border-slate-800">
        {steps.map((s, i) => (
          <div key={s.title} className="p-10 lg:p-14 border-b md:border-b-0 md:border-r border-slate-800 fade-up" style={{ animationDelay: `${0.1 * i}s` }}>
            <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
            <s.icon className="w-6 h-6 text-blue-500 mt-6" />
            <h3 className="font-cabinet font-bold text-white text-2xl mt-4">{s.title}</h3>
            <p className="mt-3 text-slate-400 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </section>

      <footer className="px-6 lg:px-12 py-10 font-mono text-xs text-slate-600">
        Internal preparation tool — not legal advice. Always tell the truth.
      </footer>
    </div>
  );
}
